// EIA forward-looking projections, baked at build time — the tool makes no
// runtime API calls. Regenerate roughly yearly when a new AEO drops (spring)
// and opportunistically when STEO moves materially.
//
// AEO source: Annual Energy Outlook 2026, Counterfactual Baseline (cb2026,
// April 2026). Division escalators are the nominal residential electricity
// CAGR 2027→2045: real $/MMBtu paths from supplemental tables 3.1–3.9
// (https://www.eia.gov/outlooks/aeo/supplement/excel/suptab_3.1.xlsx …3.9)
// times the national deflator implied by Table 8's nominal/real series
// (https://www.eia.gov/outlooks/aeo/excel/aeotab8.xlsx). Extracted 2026-08-20
// with a dependency-free XML parse (see PR #1764).
//
// STEO source: Short-Term Energy Outlook, Aug 2026 — national residential
// price forecast (+5.8% for 2026, +1.6% for 2027).
// https://www.eia.gov/outlooks/steo/report/elec_coal_renew.php
//
// Known bias, disclosed in ProjectionMethodology: EIA has recently tended to
// UNDER-project residential rate growth, so this path is conservative.

export type CensusDivision =
  | 'EAST_NORTH_CENTRAL'
  | 'EAST_SOUTH_CENTRAL'
  | 'MIDDLE_ATLANTIC'
  | 'MOUNTAIN'
  | 'NEW_ENGLAND'
  | 'PACIFIC'
  | 'SOUTH_ATLANTIC'
  | 'WEST_NORTH_CENTRAL'
  | 'WEST_SOUTH_CENTRAL';

/** Nominal residential ¢/kWh CAGR 2027→2045, AEO2026 Counterfactual Baseline */
export const divisionEscalator: Record<CensusDivision, number> = {
  EAST_NORTH_CENTRAL: 0.021,
  EAST_SOUTH_CENTRAL: 0.0211,
  MIDDLE_ATLANTIC: 0.0252,
  MOUNTAIN: 0.0237,
  NEW_ENGLAND: 0.0217,
  PACIFIC: 0.0224,
  SOUTH_ATLANTIC: 0.0217,
  WEST_NORTH_CENTRAL: 0.0251,
  WEST_SOUTH_CENTRAL: 0.0261,
};

/** National fallback for a state missing from the mapping (same computation, Table 8) */
export const NATIONAL_ESCALATOR = 0.0234;

/** EIA STEO national residential YoY forecast — owns the years before the AEO path */
export const steoForecast: Record<number, number> = {
  2026: 0.058,
  2027: 0.016,
};

// Census Bureau state→division assignment (fixed; AK/HI sit in Pacific for
// AEO end-use prices — noted in the methodology copy)
export const stateToDivision: Record<string, CensusDivision> = {
  AK: 'PACIFIC',
  AL: 'EAST_SOUTH_CENTRAL',
  AR: 'WEST_SOUTH_CENTRAL',
  AZ: 'MOUNTAIN',
  CA: 'PACIFIC',
  CO: 'MOUNTAIN',
  CT: 'NEW_ENGLAND',
  DC: 'SOUTH_ATLANTIC',
  DE: 'SOUTH_ATLANTIC',
  FL: 'SOUTH_ATLANTIC',
  GA: 'SOUTH_ATLANTIC',
  HI: 'PACIFIC',
  IA: 'WEST_NORTH_CENTRAL',
  ID: 'MOUNTAIN',
  IL: 'EAST_NORTH_CENTRAL',
  IN: 'EAST_NORTH_CENTRAL',
  KS: 'WEST_NORTH_CENTRAL',
  KY: 'EAST_SOUTH_CENTRAL',
  LA: 'WEST_SOUTH_CENTRAL',
  MA: 'NEW_ENGLAND',
  MD: 'SOUTH_ATLANTIC',
  ME: 'NEW_ENGLAND',
  MI: 'EAST_NORTH_CENTRAL',
  MN: 'WEST_NORTH_CENTRAL',
  MO: 'WEST_NORTH_CENTRAL',
  MS: 'EAST_SOUTH_CENTRAL',
  MT: 'MOUNTAIN',
  NC: 'SOUTH_ATLANTIC',
  ND: 'WEST_NORTH_CENTRAL',
  NE: 'WEST_NORTH_CENTRAL',
  NH: 'NEW_ENGLAND',
  NJ: 'MIDDLE_ATLANTIC',
  NM: 'MOUNTAIN',
  NV: 'MOUNTAIN',
  NY: 'MIDDLE_ATLANTIC',
  OH: 'EAST_NORTH_CENTRAL',
  OK: 'WEST_SOUTH_CENTRAL',
  OR: 'PACIFIC',
  PA: 'MIDDLE_ATLANTIC',
  RI: 'NEW_ENGLAND',
  SC: 'SOUTH_ATLANTIC',
  SD: 'WEST_NORTH_CENTRAL',
  TN: 'EAST_SOUTH_CENTRAL',
  TX: 'WEST_SOUTH_CENTRAL',
  UT: 'MOUNTAIN',
  VA: 'SOUTH_ATLANTIC',
  VT: 'NEW_ENGLAND',
  WA: 'PACIFIC',
  WI: 'EAST_NORTH_CENTRAL',
  WV: 'SOUTH_ATLANTIC',
  WY: 'MOUNTAIN',
};

export const projectionSources = {
  aeo: {
    label: 'EIA Annual Energy Outlook 2026',
    url: 'https://www.eia.gov/outlooks/aeo/',
  },
  history: {
    label: 'EIA state electricity data',
    url: 'https://www.eia.gov/electricity/data/state/',
  },
  steo: {
    label: 'EIA Short-Term Energy Outlook',
    url: 'https://www.eia.gov/outlooks/steo/',
  },
};

export const stateEscalator = (stateCode: string): number =>
  stateToDivision[stateCode] ? divisionEscalator[stateToDivision[stateCode]] : NATIONAL_ESCALATOR;

/**
 * Reader-selectable long-run scenarios (2026-08-20, per Nick — chat mock
 * approved). Each is a named, citable view, not our prediction; BLEND is the
 * chart default. Everything downstream — the chart, the totals, and the
 * state-average solar comparison — follows the reader's pick, so the numbers
 * and the picture can never disagree about where rates are headed.
 */
export type ProjectionScenario = 'AEO' | 'BLEND' | 'RECENT';

/** Mid-sentence names for the views — an acronym stays capitalized */
export const scenarioProseLabel: Record<ProjectionScenario, string> = {
  AEO: 'EIA',
  BLEND: 'blended',
  RECENT: 'recent',
};
